import { useState, useEffect, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Star, Send, Sparkles, CheckCircle2, Heart, ArrowRight, Share2 } from "lucide-react";
import { toast } from "sonner";
import EventLayout from "@/components/event/EventLayout";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const DAY_LABELS = {
  friday: "Friday · VIP Networking",
  saturday: "Saturday · Business Showers Ceremony",
  both: "Both days",
  "": "",
};

const DAY_KEY_TO_ISO = {
  friday: "2026-07-17",
  saturday: "2026-07-18",
  both: "both",
};

export default function Survey() {
  const [params] = useSearchParams();
  const dayParam = (params.get("day") || "").toLowerCase();
  const initialDay = ["friday", "saturday", "both"].includes(dayParam) ? dayParam : "";

  const [form, setForm] = useState({
    day: initialDay,
    overall_rating: 0,
    program_rating: 0,
    recommend_score: -1,
    favorite_moment: "",
    improvements: "",
    first_name: "",
    email: "",
    phone: "",
    consent_followup: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const canSubmit = form.overall_rating > 0 && form.program_rating > 0 && form.recommend_score >= 0;

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Please answer the 3 required ratings first.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/event/survey/submit", {
        ...form,
        day: DAY_KEY_TO_ISO[form.day] || form.day,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return <SurveyThanks name={form.first_name} />;

  return (
    <EventLayout showProgramBanner={false}>
      <div className="mx-auto max-w-lg px-5 py-8">
        <SurveyHeader dayLabel={DAY_LABELS[form.day]} />

        <div className="space-y-5 mt-6">
          <DaySelector value={form.day} onChange={setField("day")} />

          {/* Q1 · Overall rating */}
          <Question
            testid="q-overall"
            number={1}
            title="How would you rate the event overall?"
            required
          >
            <StarRating
              value={form.overall_rating}
              onChange={setField("overall_rating")}
              testidPrefix="star-overall"
            />
          </Question>

          {/* Q2 · Program rating */}
          <Question
            testid="q-program"
            number={2}
            title="How would you rate the program & agenda?"
            required
          >
            <StarRating
              value={form.program_rating}
              onChange={setField("program_rating")}
              testidPrefix="star-program"
            />
          </Question>

          {/* Q3 · NPS */}
          <Question
            testid="q-nps"
            number={3}
            title="How likely are you to recommend Freedom In Me Business Showers to a friend?"
            required
          >
            <NpsScale
              value={form.recommend_score}
              onChange={setField("recommend_score")}
            />
          </Question>

          {/* Q4 · Favorite */}
          <Question
            testid="q-favorite"
            number={4}
            title="Your favorite moment or takeaway?"
            hint="Optional · one line is perfect."
          >
            <input
              type="text"
              maxLength={240}
              value={form.favorite_moment}
              onChange={(e) => setField("favorite_moment")(e.target.value)}
              placeholder="e.g. When the honorees walked out to standing ovation…"
              data-testid="input-favorite"
              className="w-full h-11 px-3 rounded-xl border-2 border-emerald-100 focus:border-fuchsia-500 focus:outline-none font-[Outfit] text-sm bg-white"
            />
          </Question>

          {/* Q5 · Improvements */}
          <Question
            testid="q-improvements"
            number={5}
            title="What could we make even better next year?"
            hint="Optional · a few sentences help us fast-forward."
          >
            <textarea
              maxLength={1200}
              rows={4}
              value={form.improvements}
              onChange={(e) => setField("improvements")(e.target.value)}
              placeholder="Tell us anything — logistics, programming, venue, food, sound…"
              data-testid="input-improvements"
              className="w-full px-3 py-2 rounded-xl border-2 border-emerald-100 focus:border-fuchsia-500 focus:outline-none font-[Outfit] text-sm bg-white resize-none"
            />
          </Question>

          <IdentityPanel form={form} setField={setField} />

          <ConsentToggle
            checked={form.consent_followup}
            onChange={setField("consent_followup")}
          />

          <div className="pt-2">
            <Button
              disabled={!canSubmit || submitting}
              onClick={submit}
              data-testid="submit-survey"
              className="w-full h-14 rounded-2xl bg-fuchsia-600 hover:bg-fuchsia-700 disabled:bg-emerald-200 disabled:text-emerald-800/40 text-white font-[Outfit] font-bold text-base shadow-xl shadow-fuchsia-600/25 transition-all"
            >
              {submitting ? (
                <>Sending…</>
              ) : (
                <>Share my feedback <ArrowRight size={16} className="ml-1.5" /></>
              )}
            </Button>
            <p className="mt-3 text-[11px] font-[Outfit] text-emerald-950/50 text-center">
              Ratings are required · text answers are optional · your privacy matters.
            </p>
          </div>
        </div>
      </div>
    </EventLayout>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────
function SurveyHeader({ dayLabel }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.28em] font-[Outfit] font-bold uppercase text-fuchsia-700">
        {dayLabel ? `Survey · ${dayLabel}` : "Event Survey"}
      </div>
      <h1 className="font-[Cormorant_Garamond] text-4xl leading-tight text-emerald-950 mt-1">
        Share your Business Showers experience
      </h1>
      <p className="mt-2 text-sm font-[Outfit] text-emerald-950/70">
        Five short questions — under two minutes. Your voice fast-forwards the next class of founders.
      </p>
    </div>
  );
}

function DaySelector({ value, onChange }) {
  const options = [
    { k: "friday", label: "Friday" },
    { k: "saturday", label: "Saturday" },
    { k: "both", label: "Both days" },
  ];
  return (
    <div className="rounded-2xl border-2 border-emerald-100 bg-white p-4">
      <div className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-emerald-800/70 mb-2">
        Which day are you rating?
      </div>
      <div className="grid grid-cols-3 gap-2">
        {options.map((o) => (
          <button
            key={o.k}
            type="button"
            onClick={() => onChange(o.k)}
            data-testid={`day-${o.k}`}
            className={`h-11 rounded-xl border-2 text-sm font-[Outfit] font-semibold transition-all ${
              value === o.k
                ? "border-fuchsia-600 bg-fuchsia-600 text-white shadow-md shadow-fuchsia-600/30"
                : "border-emerald-100 bg-white text-emerald-950 hover:border-emerald-300"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Question({ number, title, hint, required, children, testid }) {
  return (
    <div
      className="rounded-2xl border-2 border-emerald-100 bg-white p-4"
      data-testid={testid}
    >
      <div className="flex items-start gap-3 mb-3">
        <span className="shrink-0 h-7 w-7 rounded-full bg-emerald-950 text-white text-xs font-[Outfit] font-bold flex items-center justify-center">
          {number}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-[Cormorant_Garamond] text-lg text-emerald-950 leading-tight">
            {title}
            {required && <span className="text-fuchsia-600 ml-1">*</span>}
          </div>
          {hint && (
            <div className="mt-1 text-[11px] font-[Outfit] text-emerald-950/50">{hint}</div>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

function StarRating({ value = 0, onChange, testidPrefix }) {
  const [hover, setHover] = useState(0);
  const displayed = hover || value;
  const label = ["", "Missed the mark", "Room to grow", "Solid", "Loved it", "Extraordinary"][displayed] || "Tap a star";
  return (
    <div className="flex flex-col items-center gap-2 py-1">
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            data-testid={`${testidPrefix}-${n}`}
            aria-label={`Rate ${n} out of 5`}
            className="p-1.5 focus:outline-none transition-transform hover:scale-125 active:scale-95"
          >
            <Star
              size={36}
              fill={n <= displayed ? "#ED1C7A" : "transparent"}
              strokeWidth={1.5}
              className={n <= displayed ? "text-fuchsia-600" : "text-emerald-950/25"}
            />
          </button>
        ))}
      </div>
      <div className="text-[11px] font-[Outfit] font-semibold tracking-wide uppercase text-emerald-950/60 min-h-[16px]">
        {label}
      </div>
    </div>
  );
}

function NpsScale({ value = -1, onChange }) {
  const nums = Array.from({ length: 11 }, (_, i) => i);
  return (
    <div>
      <div className="grid grid-cols-11 gap-1">
        {nums.map((n) => {
          const active = n === value;
          const color =
            n <= 6 ? "border-fuchsia-300 text-fuchsia-700"
            : n <= 8 ? "border-emerald-300 text-emerald-800"
            : "border-emerald-500 text-emerald-900";
          const activeColor =
            n <= 6 ? "bg-fuchsia-600 text-white border-fuchsia-700 shadow-md shadow-fuchsia-600/30"
            : n <= 8 ? "bg-emerald-500 text-white border-emerald-600 shadow-md shadow-emerald-600/30"
            : "bg-emerald-700 text-white border-emerald-800 shadow-md shadow-emerald-700/30";
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              data-testid={`nps-${n}`}
              className={`h-10 rounded-lg border-2 text-sm font-[Outfit] font-bold transition-all ${active ? activeColor : `bg-white ${color} hover:scale-105`}`}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div className="flex justify-between mt-2 text-[10px] font-[Outfit] font-semibold uppercase tracking-widest text-emerald-950/50">
        <span>Not likely</span>
        <span>Very likely</span>
      </div>
    </div>
  );
}

function IdentityPanel({ form, setField }) {
  return (
    <div className="rounded-2xl border-2 border-emerald-100 bg-emerald-50/40 p-4">
      <div className="flex items-start gap-3 mb-2">
        <span className="shrink-0 h-7 w-7 rounded-full bg-emerald-950/10 text-emerald-950 flex items-center justify-center">
          <Sparkles size={13} />
        </span>
        <div>
          <div className="font-[Cormorant_Garamond] text-lg text-emerald-950 leading-tight">
            Who are we thanking? <span className="text-[11px] font-[Outfit] text-emerald-950/50 uppercase tracking-widest">Optional</span>
          </div>
          <div className="text-[11px] font-[Outfit] text-emerald-950/60 mt-0.5">
            Leave blank to stay anonymous. Add your name and we&apos;ll link your feedback to your check-in record.
          </div>
        </div>
      </div>
      <div className="grid gap-2 mt-3">
        <input
          type="text"
          value={form.first_name}
          onChange={(e) => setField("first_name")(e.target.value)}
          placeholder="First name"
          maxLength={80}
          data-testid="input-first-name"
          className="w-full h-11 px-3 rounded-xl border-2 border-emerald-100 focus:border-fuchsia-500 focus:outline-none font-[Outfit] text-sm bg-white"
        />
        <input
          type="email"
          value={form.email}
          onChange={(e) => setField("email")(e.target.value)}
          placeholder="Email"
          maxLength={160}
          data-testid="input-email"
          className="w-full h-11 px-3 rounded-xl border-2 border-emerald-100 focus:border-fuchsia-500 focus:outline-none font-[Outfit] text-sm bg-white"
        />
        <input
          type="tel"
          value={form.phone}
          onChange={(e) => setField("phone")(e.target.value)}
          placeholder="Mobile (US)"
          maxLength={20}
          data-testid="input-phone"
          className="w-full h-11 px-3 rounded-xl border-2 border-emerald-100 focus:border-fuchsia-500 focus:outline-none font-[Outfit] text-sm bg-white"
        />
      </div>
    </div>
  );
}

function ConsentToggle({ checked, onChange }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid="consent-followup"
        className="mt-0.5 h-4 w-4 accent-fuchsia-600 shrink-0"
      />
      <div className="text-xs font-[Outfit] text-emerald-950/70">
        I&apos;m open to a personal thank-you or follow-up from the Freedom In Me team.
      </div>
    </label>
  );
}

function SurveyThanks({ name }) {
  return (
    <EventLayout showProgramBanner={false}>
      <div className="mx-auto max-w-lg px-5 py-16">
        <div className="rounded-3xl border-2 border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 via-white to-emerald-50 p-8 text-center shadow-2xl shadow-fuchsia-600/10">
          <div className="mx-auto h-16 w-16 rounded-full bg-fuchsia-600 text-white flex items-center justify-center mb-4 shadow-lg shadow-fuchsia-600/40">
            <CheckCircle2 size={32} />
          </div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-[Outfit] font-bold text-fuchsia-700">
            Thank you {name ? `· ${name}` : ""}
          </div>
          <h1 className="font-[Cormorant_Garamond] text-4xl leading-tight text-emerald-950 mt-2">
            Your voice is fast-forwarding what&apos;s next.
          </h1>
          <p className="mt-3 text-sm font-[Outfit] text-emerald-950/70 max-w-xs mx-auto">
            Every word gets read. We use your feedback to make the next class of founders&apos; experience even more extraordinary.
          </p>

          <div className="mt-6 flex flex-col gap-2">
            <Link
              to="/event"
              className="w-full h-12 rounded-xl bg-emerald-950 hover:bg-emerald-900 text-white font-[Outfit] font-bold text-sm flex items-center justify-center gap-2 shadow-md"
              data-testid="thanks-back-home"
            >
              <Heart size={14} /> Back to the Event Hub
            </Link>
            <Link
              to="/event/photos"
              className="w-full h-12 rounded-xl border-2 border-fuchsia-300 text-fuchsia-800 hover:bg-fuchsia-50 font-[Outfit] font-bold text-sm flex items-center justify-center gap-2"
              data-testid="thanks-upload-photos"
            >
              <Share2 size={14} /> Upload your photos
            </Link>
          </div>
        </div>
      </div>
    </EventLayout>
  );
}
